import { useState } from 'react';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { IconStarFilled } from '@xing-com/icons';

import { PeakAndPopCompany } from '../../components/peak-and-pop';
import { useDeckCardTyped, useSearchAndShuffleContext } from '../../context';

import Styles from './company.style';
import { getSalary } from './salary-tile';

export const Company: React.FC = () => {
  const [showPeakAndPop, setShowPeakAndPop] = useState(false);
  const { data } = useDeckCardTyped(['TakenJob', 'AvailableJob'] as const);
  const { cardLinksEnabled } = useSearchAndShuffleContext();
  const { isLoggedOut } = useLoginState();

  const company = data?.company || data?.fallbackCompany;
  const logoImage = data?.company?.logos?.logo256px;
  const kununuData = data?.company?.kununuData;
  const aboutUs =
    data?.company?.aboutUsBatched?.__typename === 'AboutEntity'
      ? data.company?.aboutUsBatched
      : null;

  const hasLink = !!data?.company?.entityPage || !!aboutUs;
  const handleClick = (e: React.MouseEvent): void => {
    if (cardLinksEnabled.current && hasLink) {
      e.preventDefault();
      e.stopPropagation();
      setShowPeakAndPop(true);
    }
  };

  if (!company) {
    return null;
  }

  const getDescriptionExtraLines = (): number => {
    let highlightsCounter;
    let extra = 0;

    if (isLoggedOut) {
      highlightsCounter = (data.paid ? 1 : 0) + (data.employmentType ? 1 : 0);
    } else {
      highlightsCounter =
        (data.matchingHighlights?.matchingFacts.length || 0) +
        (data.matchingHighlights?.nonMatchingFacts.length || 0);
    }

    if (data.type === 'TakenJob') {
      extra = extra + 1;
    }

    if (data.type === 'AvailableJob') {
      extra = extra + 1;
      if (highlightsCounter === 0) {
        extra = extra + 3.5;
      } else if (highlightsCounter < 3) {
        extra = extra + 1.5;
      }
    }

    if (getSalary(data) === null) {
      extra = extra + 1.5;
    }

    if (!data.skillsTotal && !isLoggedOut) {
      extra = extra + 1.5;
    }

    return Math.ceil(extra);
  };

  return (
    <>
      <Styles.CompanyLink onClick={handleClick} dataQa="dm-tile-company">
        <Styles.Company>
          <Styles.Logo
            src={logoImage || undefined}
            size="xsmall"
            type="company"
            alt={`${company?.companyName}`}
          />
          <Styles.CompanyText>
            <Styles.Name size="small">{company?.companyName}</Styles.Name>
            {data?.location && (
              <Styles.LocationContainer>
                <Styles.Location size="small">{data?.location}</Styles.Location>
              </Styles.LocationContainer>
            )}
          </Styles.CompanyText>
          {kununuData?.ratingAverage && (
            <Styles.KununuValue icon={IconStarFilled}>
              {kununuData?.ratingAverage}
            </Styles.KununuValue>
          )}
        </Styles.Company>
        {aboutUs?.aboutSummary && (
          <Styles.CompanyDescription
            size="small"
            $extraLines={getDescriptionExtraLines()}
          >
            {aboutUs?.aboutSummary}
          </Styles.CompanyDescription>
        )}
      </Styles.CompanyLink>

      <PeakAndPopCompany
        isShown={showPeakAndPop}
        setIsShown={setShowPeakAndPop}
        trackTileName="company"
      />
    </>
  );
};
